import { useEffect, useRef, useState } from 'react';

/**
 * Hover-state hook for info popovers: open immediately on enter, close
 * after a short grace period so the user can transit from the trigger
 * to the popover body without losing it. Bind `open` and
 * `scheduleClose` to both the trigger and the popover itself (so
 * re-entering the popover cancels the pending close).
 */
export const useTooltipHoverState = (closeDelayMs = 200) => {
  const [isOpen, setIsOpen] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  const open = () => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const scheduleClose = () => {
    if (hideTimeoutRef.current !== null) {
      window.clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      hideTimeoutRef.current = null;
    }, closeDelayMs);
  };

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return { isOpen, open, scheduleClose };
};
